import { useContext, useMemo, useState, type ComponentType, type ReactNode } from 'react'
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined'
import TaskAlt from '@mui/icons-material/TaskAlt'
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined'
import CalendarMonth from '@mui/icons-material/CalendarMonth'
import ChatBubbleOutlineOutlined from '@mui/icons-material/ChatBubbleOutlineOutlined'
import ChatBubble from '@mui/icons-material/ChatBubble'
import GroupsOutlined from '@mui/icons-material/GroupsOutlined'
import Groups from '@mui/icons-material/Groups'
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined'
import LocationOn from '@mui/icons-material/LocationOn'
import SchoolOutlined from '@mui/icons-material/SchoolOutlined'
import School from '@mui/icons-material/School'
import WorkOutline from '@mui/icons-material/WorkOutlineRounded'
import Work from '@mui/icons-material/WorkRounded'
import { MobileTabBar, MobileTabRow, type MobileTabItem } from '../Common/MobileTabs'
import { MoreTabsSheet } from '../Common/MoreTabsSheet'
import { TabBarEditor } from '../Common/TabBarEditor'
import type { HelperTabKey as StoreHelperTabKey } from '../../services/helper/helperStore'
import {
  getTabDef,
  getTabLabelKey,
  helperCatalogToStoreKey,
  helperStoreToCatalogKey,
  type HelperTabKey as CatalogHelperTabKey,
} from '../../services/navigation/tabCatalog'
import { useHelperTabBar } from '../../services/navigation/useHelperTabBar'
import type { TabBarPolicy } from '../../services/navigation/tabBarEngine'
import { WorkspaceContext } from '../../context/workspaceContextCore'
import { useNls } from '../../nls/nlsProvider'
import { getHelperTabLabel } from '../Mobile/MobileNls'

type IconPair = {
  outlined: ComponentType<{ className?: string; style?: React.CSSProperties; fontSize?: 'small' | 'inherit' | 'large' | 'medium' }>
  filled: ComponentType<{ className?: string; style?: React.CSSProperties; fontSize?: 'small' | 'inherit' | 'large' | 'medium' }>
}

const ICONS: Record<StoreHelperTabKey, IconPair> = {
  TASKS: { outlined: TaskAltOutlined, filled: TaskAlt },
  CALENDAR: { outlined: CalendarMonthOutlined, filled: CalendarMonth },
  MEETINGS: { outlined: GroupsOutlined, filled: Groups },
  CHAT: { outlined: ChatBubbleOutlineOutlined, filled: ChatBubble },
  FIELD: { outlined: LocationOnOutlined, filled: LocationOn },
  SCHOOL: { outlined: SchoolOutlined, filled: School },
  OPS: { outlined: WorkOutline, filled: Work },
}

function getHelperTabIcons(key: CatalogHelperTabKey | string): IconPair {
  const storeKey = helperCatalogToStoreKey(key as CatalogHelperTabKey)
  if (key === 'school') return ICONS.SCHOOL
  if (key === 'ops') return ICONS.OPS
  return ICONS[storeKey] ?? ICONS.TASKS
}

type Props = {
  activeTab: StoreHelperTabKey
  onChange: (tab: StoreHelperTabKey) => void
  onEnterFocus: () => void
  unreadByTab: Record<StoreHelperTabKey, boolean>
  hiddenTabs?: ReadonlySet<StoreHelperTabKey>
  onHeightChange?: (height: number) => void
  uid?: string
  workspaceId?: string
  secondaryTabs?: ReactNode
}

export const HelperBottomNav = ({
  activeTab,
  onChange,
  onEnterFocus,
  unreadByTab,
  hiddenTabs,
  onHeightChange,
  uid,
  workspaceId,
  secondaryTabs,
}: Props) => {
  const { t } = useNls()
  const [moreSheetOpen, setMoreSheetOpen] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)

  const catalogActiveTab = helperStoreToCatalogKey(activeTab)

  const policy = useMemo<TabBarPolicy | undefined>(() => {
    if (!hiddenTabs) return undefined
    return {
      hidden: Array.from(hiddenTabs).map((key) => helperStoreToCatalogKey(key)),
      lockedVisible: [],
    }
  }, [hiddenTabs])

  const unreadCatalogRecord = useMemo<Partial<Record<CatalogHelperTabKey, boolean>>>(() => {
    if (!unreadByTab) return {}
    return {
      tasks: unreadByTab.TASKS,
      calendar: unreadByTab.CALENDAR,
      meetings: unreadByTab.MEETINGS,
      chat: unreadByTab.CHAT,
      field: unreadByTab.FIELD,
      school: unreadByTab.SCHOOL,
      ops: unreadByTab.OPS,
    }
  }, [unreadByTab])

  const workspaceContext = useContext(WorkspaceContext)
  const currentWorkspace = workspaceContext?.workspaces.find(
    (w) => w.id === (workspaceId || workspaceContext?.activeWorkspaceId),
  )
  const useCase = currentWorkspace?.onboardingSetup?.useCase || undefined

  const {
    pinned,
    more,
    locked,
    moreHasUnread,
    isTabUnread,
    savePersonalTabs,
  } = useHelperTabBar({
    uid,
    workspaceId,
    role: 'HELPER',
    policy,
    useCase,
    unreadByTab: unreadCatalogRecord,
  })

  const isTabInMore = more.includes(catalogActiveTab)

  const items: Array<MobileTabItem<CatalogHelperTabKey>> = pinned.map((tabKey) => {
    const iconPair = getHelperTabIcons(tabKey)
    const OutlinedIcon = iconPair.outlined
    const FilledIcon = iconPair.filled
    const isFixed = tabKey === 'tasks'
    const tabDef = getTabDef(tabKey, 'helper')
    const labelKey = tabDef ? getTabLabelKey(tabDef, { workspace: currentWorkspace, modules: currentWorkspace?.modules }) : undefined
    return {
      key: tabKey,
      label: labelKey ? t(labelKey) : getHelperTabLabel(tabKey, t),
      icon: <OutlinedIcon fontSize="small" style={{ fontSize: '22px' }} />,
      activeIcon: <FilledIcon fontSize="small" style={{ fontSize: '22px' }} />,
      unread: isTabUnread(tabKey),
      testId: `helper-tab-${tabKey}`,
      // Phase D contract: onLongPress: tab === 'TASKS' ? onEnterFocus : undefined
      onLongPress: isFixed ? onEnterFocus : () => setEditorOpen(true),
    }
  })

  return (
    <>
      <MobileTabBar
        ariaLabel={t('NLSHelperAriaNavigation')}
        testId="helper-bottom-nav"
        hasSecondaryRow={Boolean(secondaryTabs)}
        onHeightChange={onHeightChange}
      >
        {secondaryTabs}
        <MobileTabRow
          items={items}
          activeKey={isTabInMore ? undefined : catalogActiveTab}
          onChange={(key) => onChange(helperCatalogToStoreKey(key))}
          ariaLabel={t('NLSHelperAriaTabs')}
          more={more.length > 0 ? {
            label: t('NLSCommonTabMore'),
            isActive: isTabInMore,
            unread: moreHasUnread,
            onClick: () => setMoreSheetOpen(true),
            testId: 'helper-tab-more',
          } : undefined}
        />
      </MobileTabBar>

      <MoreTabsSheet
        isOpen={moreSheetOpen}
        onClose={() => setMoreSheetOpen(false)}
        tabs={more.map((key) => {
          const iconPair = getHelperTabIcons(key)
          const IconComp = iconPair.outlined
          const lockedItem = locked.find((l) => l.key === key)
          const tabDef = getTabDef(key, 'helper')
          const labelKey = tabDef ? getTabLabelKey(tabDef, { workspace: currentWorkspace, modules: currentWorkspace?.modules }) : undefined
          return {
            key,
            label: labelKey ? t(labelKey) : getHelperTabLabel(key, t),
            icon: <IconComp fontSize="small" style={{ fontSize: '22px' }} />,
            unread: isTabUnread(key),
            locked: Boolean(lockedItem),
            lockReason: lockedItem ? t(lockedItem.reasonKey) : undefined,
            testId: `helper-more-tab-${key}`,
          }
        })}
        onSelectTab={(key) => {
          setMoreSheetOpen(false)
          onChange(helperCatalogToStoreKey(key as CatalogHelperTabKey))
        }}
        onOpenEdit={() => {
          setMoreSheetOpen(false)
          setEditorOpen(true)
        }}
      />

      <TabBarEditor
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        pinned={pinned}
        more={more}
        locked={locked.map((l) => ({ key: l.key as CatalogHelperTabKey, reason: t(l.reasonKey) }))}
        labels={(key) => getHelperTabLabel(key, t)}
        icons={(key) => {
          const iconPair = getHelperTabIcons(key as CatalogHelperTabKey)
          const IconComp = iconPair.outlined
          return <IconComp fontSize="small" style={{ fontSize: '20px' }} />
        }}
        onSave={(next) => void savePersonalTabs(next as CatalogHelperTabKey[])}
        onResetToDefault={() => void savePersonalTabs([])}
      />
    </>
  )
}
